import React, { useState, useEffect } from 'react';
import Layout from './components/Layout';
import Contatti from './components/Contatti';
import Magazzini from './components/Magazzini';
import Viaggi from './components/Viaggi';
import Report from './components/Report';
import AdvancedBackup from './components/AdvancedBackup';
import PasswordResetForm from './components/PasswordResetForm';
import ChangePasswordForm from './components/ChangePasswordForm';
import NewPasswordForm from './components/NewPasswordForm';
import { useAuth } from './hooks/useSupabase';
import supabase from './services/supabase';

const LoginForm = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showResetForm, setShowResetForm] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Inserisci email e password');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await onLogin(email, password);
    } catch (err) {
      setError('Login fallito: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  if (showResetForm) {
    return <PasswordResetForm onBack={() => setShowResetForm(false)} />;
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Truck Transport Management
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Accedi al tuo account
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email-address" className="sr-only">Indirizzo Email</label>
              <input
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Indirizzo Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">Password</label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>
          
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}
          
          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {loading ? 'Accesso in corso...' : 'Accedi'}
            </button>
          </div>
          
          <div className="text-center">
            <button
              type="button"
              onClick={() => setShowResetForm(true)}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Password dimenticata?
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const App = () => {
  const [activeTab, setActiveTab] = useState('viaggi');
  const [showChangePassword, setShowChangePassword] = useState(false);
  const { user, loading, signIn, signOut } = useAuth();
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  
  // Controlla se l'URL contiene un token di reset password
  useEffect(() => {
    // Funzione per verificare se siamo in un flusso di reset password
    const checkPasswordReset = async () => {
      // Ottieni l'hash dall'URL (il token è solitamente nella parte hash)
      const hash = window.location.hash;
      const query = window.location.search;
      
      // Verifica se l'URL contiene parametri tipici del reset password
      if ((hash && hash.includes('type=recovery')) || (query && query.includes('type=recovery'))) {
        // Siamo in un flusso di reset password
        setIsPasswordReset(true);
        
        // Verifica che la sessione abbia recepito il token (lo fa automaticamente Supabase)
        try {
          const { data, error } = await supabase.auth.getSession();
          
          // Se c'è un errore o non c'è una sessione attiva, 
          // il token potrebbe non essere valido o scaduto
          if (error || !data.session) {
            setIsPasswordReset(false);
          }
        } catch (err) {
          console.error("Errore durante la verifica della sessione:", err);
          setIsPasswordReset(false);
        }
      }
    };
    
    checkPasswordReset();
  }, []);
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-xl text-gray-600">Caricamento...</div>
      </div>
    );
  }
  
  // Se siamo in un flusso di reset password, mostra il form per impostare la nuova password
  if (isPasswordReset) {
    return <NewPasswordForm redirectToLogin={() => {
      // Pulisce l'URL dai parametri di reset e reimposta lo stato
      window.history.replaceState({}, document.title, '/');
      setIsPasswordReset(false);
    }} />;
  }
  
  // Se l'utente non è autenticato, mostra il form di login
  if (!user) {
    return <LoginForm onLogin={signIn} />;
  }
  
  const handleLogout = async () => {
    try {
      await signOut();
    } catch (err) {
      console.error("Errore durante il logout:", err);
    }
  };
  
  return (
    <>
      <Layout 
        activeTab={activeTab} 
        setActiveTab={setActiveTab} 
        onUserClick={() => setShowChangePassword(true)}
        onLogout={handleLogout}
      >
        {activeTab === 'viaggi' && <Viaggi />}
        {activeTab === 'contatti' && <Contatti />}
        {activeTab === 'magazzini' && <Magazzini />}
        {activeTab === 'report' && <Report />}
        {activeTab === 'backup' && <AdvancedBackup />}
      </Layout>
      
      {showChangePassword && (
        <ChangePasswordForm onClose={() => setShowChangePassword(false)} />
      )}
    </>
  );
};

export default App;